import type { LucideIcon } from "lucide-react";
import CountUp from "react-countup";

import { cn } from "@/utils/cn";
import { Skeleton } from "./ui/skeleton";

type StatIcon =
  string | LucideIcon | React.FunctionComponent<React.SVGProps<SVGSVGElement>>;

export type PageDataStat = {
  name: string;
  value: number | string;
  icon: StatIcon;
  iconClassName?: string;
  /**
   * Makes the tile a filter control. Spread `useTableParams().statFilter({...})`
   * to get both of these at once — see CLAUDE.md, "Stat tiles as filters".
   * A tile with no `onSelect` stays a plain read-out.
   */
  onSelect?: () => void;
  /** The table is currently showing exactly this tile's slice. */
  active?: boolean;
};

type IProps = {
  data: PageDataStat[];
  className?: string;
  loading?: boolean;
};

const ICON_CHIP_CLASS =
  "flex items-center justify-center rounded-xs shrink-0 bg-primary/10 p-1";
const ICON_CHIP_DESKTOP = "hidden sm:flex h-12 w-12";
const ICON_CHIP_MOBILE = "flex sm:hidden h-10 w-10";
const ICON_INNER_DESKTOP = "size-full";
const ICON_INNER_MOBILE = "size-full";

const TILE_BASE =
  "flex items-center justify-between gap-2 w-full bg-white rounded-sm border p-4 py-3 text-start transition-colors";
/** "This one is a control" — separate from, and shown alongside, "selected". */
const TILE_SELECTABLE =
  "cursor-pointer hover:border-primary/40 hover:bg-primary/[0.03] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/40";
/**
 * `border-primary bg-primary/5` is the selected-card treatment used elsewhere
 * (CheckInScopeSelector, EmployeePickerSingle); the ring doubles the border so
 * the selection still reads at a glance across a row of white tiles.
 */
const TILE_ACTIVE = "border-primary ring-1 ring-primary bg-primary/5";

const StatIconRender = ({
  icon,
  name,
  className,
}: {
  icon: StatIcon;
  name: string;
  className: string;
}) => {
  if (typeof icon === "string") {
    return (
      <img
        src={icon}
        alt={name}
        draggable={false}
        className={cn(className, "object-contain")}
      />
    );
  }
  const IconComponent = icon;
  return <IconComponent className={className} />;
};

export const PageDataStats = ({ data, className, loading }: IProps) => {
  return (
    <div className={cn("gap-4 grid grid-cols-2 lg:grid-cols-4", className)}>
      {data.map((item) => {
        const numericValue = Number(item.value);
        const isNumber = !isNaN(numericValue);
        const selectable = !!item.onSelect;

        const content = (
          <>
            <div className="flex flex-col gap-1 w-full">
              <div className="flex justify-between w-full">
                <h6 className="font-semibold text-lg md:text-xl font-sans">
                  {loading && !isNumber ? (
                    <Skeleton className="w-5 h-5 mx-auto text-primary" />
                  ) : isNumber ? (
                    <CountUp
                      className="account-balance"
                      start={0}
                      end={numericValue}
                      duration={1}
                      useEasing
                      separator=","
                    />
                  ) : (
                    item.value
                  )}
                </h6>
                <div className={cn(ICON_CHIP_CLASS, ICON_CHIP_MOBILE)}>
                  <StatIconRender
                    icon={item.icon}
                    name={item.name}
                    className={ICON_INNER_MOBILE}
                  />
                </div>
              </div>
              <p className="text-muted-foreground text-[10px] md:text-sm">
                {item.name}
              </p>
            </div>
            <div className={cn(ICON_CHIP_CLASS, ICON_CHIP_DESKTOP)}>
              <StatIconRender
                icon={item.icon}
                name={item.name}
                className={ICON_INNER_DESKTOP}
              />
            </div>
          </>
        );

        const classes = cn(
          TILE_BASE,
          selectable && TILE_SELECTABLE,
          selectable && item.active && TILE_ACTIVE,
        );

        // A non-selectable tile stays a `div`: a disabled button would still be
        // in the tab order in some browsers, and there is nothing to announce.
        return selectable ? (
          <button
            key={item.name}
            type="button"
            aria-pressed={!!item.active}
            onClick={item.onSelect}
            className={classes}
          >
            {content}
          </button>
        ) : (
          <div key={item.name} className={classes}>
            {content}
          </div>
        );
      })}
    </div>
  );
};
